import {
  EMISSIONS_PER_TREE_IN_LBS_OF_CO2,
  EMISSIONS_PER_TREE_IN_KG_OF_CO2,
  ELECTRICITY_EMISSIONS_IN_LBS_PER_MWH,
  ELECTRICITY_EMISSIONS_IN_KG_PER_MWH,
  GASOLINE_EMISSIONS_IN_LBS_PER_GAL,
  GASOLINE_EMISSIONS_IN_KG_PER_LITER
} from "../../../client_customizations/data/assumptions/ASSUMPTIONS";
import isBEV from "../../../functions/vehicle/isBEV";
import isPHEV from "../../../functions/vehicle/isPHEV";

const calcEmissions = {
  electricEmissions: function (consumptionRate, distance, country, userPrefs) {
    const electricEmissions = userPrefs?.get("electricEmissions") ||
      (country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM
        ? ELECTRICITY_EMISSIONS_IN_KG_PER_MWH.value
        : ELECTRICITY_EMISSIONS_IN_LBS_PER_MWH.value);
    return (electricEmissions * consumptionRate * distance) / 1000 / 100;
  },

  gasolineEmissions: function (consumptionRate, distance, country, userPrefs) {
    const gasolineEmissions = (userPrefs?.get("gasolineEmissions") / 100) ||
      (country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM
        ? GASOLINE_EMISSIONS_IN_KG_PER_LITER.value
        : GASOLINE_EMISSIONS_IN_LBS_PER_GAL.value);
    return country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM ?
      (gasolineEmissions * distance * consumptionRate) / 100 :
      (gasolineEmissions * distance) / consumptionRate;
  },

  emissions: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    country,
    userPrefs
  ) {
    if (!vehicle) return 0;
    let emissions = 0;

    if (!isBEV(vehicle) && !isPHEV(vehicle)) {
      emissions = this.gasolineEmissions(
        vehicle.fossil_fuel_efficiency,
        milesDrivenAnnually,
        country,
        userPrefs
      );
    }

    if (isBEV(vehicle)) {
      emissions = this.electricEmissions(
        vehicle.electric_efficiency,
        milesDrivenAnnually,
        country,
        userPrefs
      );
    }

    if (isPHEV(vehicle)) {
      emissions =
        this.electricEmissions(
          vehicle.electric_efficiency,
          milesDrivenAnnually * (electricMilesPortionForPhev / 100),
          country,
          userPrefs
        ) +
        this.gasolineEmissions(
          vehicle.fossil_fuel_efficiency,
          milesDrivenAnnually * (1 - electricMilesPortionForPhev / 100),
          country,
          userPrefs
        );
    }
    return emissions;
  },

  emissionReduction: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    country,
    userPrefs
  ) {
    return (
      this.emissions(
        vehicle.equivalent_gas_vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        country,
        userPrefs
      ) -
      this.emissions(
        vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        country,
        userPrefs
      )
    );
  },

  treesPlanted: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    country,
    userPrefs
  ) {
    return (
      this.emissionReduction(
        vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        country,
        userPrefs
      ) /
      (country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM
        ? EMISSIONS_PER_TREE_IN_KG_OF_CO2.value
        : EMISSIONS_PER_TREE_IN_LBS_OF_CO2.value)
    );
  },

  fuelUsed: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    country
  ) {
    if (!vehicle) return 0;

    // All-electric --> no gasoline used
    if (isBEV(vehicle)) {
      return 0;
    }

    // Not electric --> all miles on gasoline
    if (!isBEV(vehicle) && !isPHEV(vehicle)) {
      return country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM ?
      milesDrivenAnnually * vehicle.fossil_fuel_efficiency / 100 :
      milesDrivenAnnually / vehicle.fossil_fuel_efficiency;
    }

    return country === "Canada" || process.env.REACT_APP_METRIC_SYSTEM ?
      ((1 - electricMilesPortionForPhev / 100) *
        milesDrivenAnnually *
        vehicle.fossil_fuel_efficiency) / 100 :
      ((1 - electricMilesPortionForPhev / 100) * milesDrivenAnnually) /
        vehicle.fossil_fuel_efficiency;
  },

  fuelSaved: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    country
  ) {
    return (
      this.fuelUsed(
        vehicle.equivalent_gas_vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        country
      ) -
      this.fuelUsed(vehicle, milesDrivenAnnually, electricMilesPortionForPhev, country)
    );
  },
};

export default calcEmissions;
